import { useMemo, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import useAuth from "../useAuth";
import useSite from "./useSite";
import useGlobalSite from "./useGlobalSite";
import {
  isAllSites,
  pickDefaultSiteId,
  requiresSingleSiteScopePath,
} from "../../utils/globalSite";

function resolveSiteUid(effectiveSiteUid, curSiteUid, effectiveSiteId, siteList, siteOptions) {
  if (effectiveSiteUid) return effectiveSiteUid;
  if (curSiteUid) return curSiteUid;
  if (effectiveSiteId && siteList?.length) {
    const fromList = siteList.find((s) => s.site_id === effectiveSiteId);
    if (fromList?.id) return fromList.id;
  }
  if (effectiveSiteId && siteOptions?.length) {
    const fromOpt = siteOptions.find((s) => s.site_id === effectiveSiteId);
    if (fromOpt?.id) return fromOpt.id;
    if (fromOpt?.site_uid) return fromOpt.site_uid;
  }
  return null;
}

/**
 * Shared site-scope state for v2 pages.
 * @param {{ mode?: 'single' | 'any', autoPickSite?: boolean }} options
 * - single: requires one concrete site (services, SSL config)
 * - any: allows "All sites" when sites exist (detection events)
 */
export default function useSiteScope({ mode = "single", autoPickSite = true } = {}) {
  const { isAuthenticated } = useAuth();
  const { siteList, getSitesForItems } = useSite();
  const { pathname } = useLocation();
  const {
    effectiveSiteUid,
    effectiveSiteId,
    curSiteUid,
    curSiteId,
    siteOptions,
    applySiteSelectorUi,
    setCurSiteId,
    sitesReady,
    apiSiteId,
    isAllSitesSelected,
  } = useGlobalSite();

  useEffect(() => {
    if (isAuthenticated && !siteList) getSitesForItems();
  }, [isAuthenticated, siteList, getSitesForItems]);

  useLayoutEffect(() => {
    if (!autoPickSite || !requiresSingleSiteScopePath(pathname)) return;
    applySiteSelectorUi({ enabled: true, showAllOption: false });
    if (!isAllSites(curSiteId) || !siteOptions?.length) return;
    const next = pickDefaultSiteId(siteOptions);
    if (next) setCurSiteId(next);
  }, [pathname, curSiteId, siteOptions, applySiteSelectorUi, setCurSiteId, autoPickSite]);

  const siteUid = useMemo(
    () => resolveSiteUid(effectiveSiteUid, curSiteUid, effectiveSiteId, siteList, siteOptions),
    [effectiveSiteUid, curSiteUid, effectiveSiteId, siteList, siteOptions]
  );

  const selectedSite = useMemo(
    () => (siteUid && siteList ? siteList.find((s) => s.id === siteUid) ?? null : null),
    [siteList, siteUid]
  );

  const status = useMemo(() => {
    if (!sitesReady) return "loading";
    if (!siteOptions?.length) return "no_sites";
    if (mode === "single") {
      if (siteOptions.length > 0 && !siteList) return "loading";
      if (siteList && !siteUid) return "no_site_selected";
    }
    return "ready";
  }, [sitesReady, siteOptions, mode, siteList, siteUid]);

  const siteScopeLoading = status === "loading";
  const siteScopeBlocked = status === "no_site_selected";

  return {
    status,
    siteUid,
    siteId: siteUid,
    siteSiteId: effectiveSiteId,
    effectiveSiteId,
    effectiveSiteUid,
    apiSiteId,
    isAllSitesSelected,
    siteList,
    siteOptions,
    selectedSite,
    sitesReady,
    siteScopeLoading,
    siteScopeBlocked,
  };
}

export { resolveSiteUid };
